import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Calendar,
  DollarSign,
  Clock,
  FileText,
  CheckCircle2,
  AlertCircle,
  Info,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
} from "lucide-react";
import { CompanyDetails as CompanyDetailsType } from "@/types/company";
import { TaxRate, TaxRateFormData, UK_VAT_RATES } from '@/types/tax-rate';
import { getAllCurrencies } from '@/types/currency';
import { useCurrency } from '@/providers/currency-provider';
import { toast } from 'sonner';

// Mock company data
const mockCompanyData: CompanyDetailsType = {
  id: "comp-001",
  code: "ACME",
  name: "Acme Corporation Ltd",
  legalName: "Acme Corporation Limited",
  registrationNumber: "12345678",
  vatNumber: "GB123456789",
  taxNumber: "123/AB45678",
  address: {
    line1: "123 Business Park",
    line2: "Innovation Drive",
    city: "London",
    county: "Greater London",
    postcode: "SW1A 1AA",
    country: "United Kingdom",
  },
  contact: {
    phone: "+44 20 1234 5678",
    email: "info@acmecorp.co.uk",
    website: "www.acmecorp.co.uk",
  },
  settings: {
    currency: "GBP",
    currencySymbol: "£",
    timezone: "Europe/London",
    dateFormat: "DD/MM/YYYY",
    fiscalYearStart: "04-01",
    fiscalYearEnd: "03-31",
  },
  integration: {
    erpSystem: "Access Financials",
    erpVersion: "2024.1",
    lastSyncDate: "2024-01-15T14:30:00Z",
    syncStatus: "connected",
  },
  metadata: {
    createdDate: "2023-01-15T10:00:00Z",
    dataSource: "Access Financials",
    lastModifiedDate: "2024-01-15T14:30:00Z",
  },
};

export function CompanySettingsPage() {
  const [activeTab, setActiveTab] = useState("company");
  const company = mockCompanyData;
  const { currency, setCurrency, formatCurrency } = useCurrency();
  const allCurrencies = getAllCurrencies();

  // Tax Rates state
  const [taxRates, setTaxRates] = useState<TaxRate[]>([
    {
      id: '1',
      name: UK_VAT_RATES.STANDARD.name,
      rate: UK_VAT_RATES.STANDARD.rate,
      description: UK_VAT_RATES.STANDARD.description,
      isDefault: true,
      isActive: true,
      createdDate: new Date(),
      modifiedDate: new Date(),
    },
    {
      id: '2',
      name: UK_VAT_RATES.REDUCED.name,
      rate: UK_VAT_RATES.REDUCED.rate,
      description: UK_VAT_RATES.REDUCED.description,
      isDefault: false,
      isActive: true,
      createdDate: new Date(),
      modifiedDate: new Date(),
    },
    {
      id: '3',
      name: UK_VAT_RATES.ZERO.name,
      rate: UK_VAT_RATES.ZERO.rate,
      description: UK_VAT_RATES.ZERO.description,
      isDefault: false,
      isActive: true,
      createdDate: new Date(),
      modifiedDate: new Date(),
    },
  ]);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTaxRate, setSelectedTaxRate] = useState<TaxRate | null>(null);
  const [formData, setFormData] = useState<TaxRateFormData>({
    name: '',
    rate: 0,
    description: '',
    isDefault: false,
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSyncStatusBadge = (status: CompanyDetailsType["integration"]["syncStatus"]) => {
    switch (status) {
      case "connected":
        return (
          <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        );
      case "syncing":
        return (
          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
            <Clock className="h-3 w-3 mr-1" />
            Syncing
          </Badge>
        );
      case "error":
        return (
          <Badge className="bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
            <AlertCircle className="h-3 w-3 mr-1" />
            Error
          </Badge>
        );
      case "disconnected":
        return (
          <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300">
            <AlertCircle className="h-3 w-3 mr-1" />
            Disconnected
          </Badge>
        );
    }
  };

  // Tax Rate handlers
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (formData.rate < 0 || formData.rate > 100) {
      errors.rate = 'Rate must be between 0 and 100';
    }

    const isDuplicate = taxRates.some(
      (rate) =>
        rate.name.toLowerCase() === formData.name.toLowerCase() &&
        rate.id !== selectedTaxRate?.id
    );
    if (isDuplicate) {
      errors.name = 'A tax rate with this name already exists';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      rate: 0,
      description: '',
      isDefault: false,
      isActive: true,
    });
    setFormErrors({});
    setIsAddDialogOpen(true);
  };

  const handleEdit = (taxRate: TaxRate) => {
    setSelectedTaxRate(taxRate);
    setFormData({
      name: taxRate.name,
      rate: taxRate.rate,
      description: taxRate.description || '',
      isDefault: taxRate.isDefault,
      isActive: taxRate.isActive,
    });
    setFormErrors({});
    setIsEditDialogOpen(true);
  };

  const handleDelete = (taxRate: TaxRate) => {
    setSelectedTaxRate(taxRate);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveAdd = () => {
    if (!validateForm()) return;

    const newTaxRate: TaxRate = {
      id: Date.now().toString(),
      name: formData.name,
      rate: formData.rate,
      description: formData.description,
      isDefault: formData.isDefault,
      isActive: formData.isActive,
      createdDate: new Date(),
      modifiedDate: new Date(),
    };

    if (formData.isDefault) {
      setTaxRates((prev) =>
        prev.map((rate) => ({ ...rate, isDefault: false }))
      );
    }

    setTaxRates((prev) => [...prev, newTaxRate]);
    setIsAddDialogOpen(false);
    toast.success(`Tax rate added: ${formData.name}`);
  };

  const handleSaveEdit = () => {
    if (!validateForm() || !selectedTaxRate) return;

    if (formData.isDefault) {
      setTaxRates((prev) =>
        prev.map((rate) =>
          rate.id === selectedTaxRate.id
            ? { ...rate, isDefault: false }
            : { ...rate, isDefault: false }
        )
      );
    }

    setTaxRates((prev) =>
      prev.map((rate) =>
        rate.id === selectedTaxRate.id
          ? {
              ...rate,
              name: formData.name,
              rate: formData.rate,
              description: formData.description,
              isDefault: formData.isDefault,
              isActive: formData.isActive,
              modifiedDate: new Date(),
            }
          : rate
      )
    );

    setIsEditDialogOpen(false);
    setSelectedTaxRate(null);
    toast.success(`Tax rate updated: ${formData.name}`);
  };

  const confirmDelete = () => {
    if (selectedTaxRate) {
      if (selectedTaxRate.isDefault) {
        toast.error('Cannot delete the default tax rate');
        return;
      }

      setTaxRates((prev) => prev.filter((rate) => rate.id !== selectedTaxRate.id));
      toast.success(`Tax rate deleted: ${selectedTaxRate.name}`);
      setIsDeleteDialogOpen(false);
      setSelectedTaxRate(null);
    }
  };

  const handleToggleActive = (taxRate: TaxRate) => {
    setTaxRates((prev) =>
      prev.map((rate) =>
        rate.id === taxRate.id
          ? { ...rate, isActive: !rate.isActive, modifiedDate: new Date() }
          : rate
      )
    );
    toast.success(
      `Tax rate ${taxRate.isActive ? 'deactivated' : 'activated'}: ${taxRate.name}`
    );
  };

  const handleSetDefault = (taxRate: TaxRate) => {
    setTaxRates((prev) =>
      prev.map((rate) => ({
        ...rate,
        isDefault: rate.id === taxRate.id,
        modifiedDate: rate.id === taxRate.id ? new Date() : rate.modifiedDate,
      }))
    );
    toast.success(`Default tax rate set to: ${taxRate.name}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Company Settings</h1>
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            🟡 Demo Mode
          </Badge>
        </div>
        <p className="text-muted-foreground mt-2">
          Company information and tax configuration
        </p>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle>Company Configuration</CardTitle>
          <CardDescription>
            View company details and manage tax rates
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="company">Company Information</TabsTrigger>
              <TabsTrigger value="tax">Tax Configuration</TabsTrigger>
            </TabsList>

            {/* Company Information Tab */}
            <TabsContent value="company" className="space-y-6 mt-6">
              {/* Info Banner */}
              <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
                <CardContent className="pt-6">
                  <div className="flex gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Read-Only Information
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Company details are automatically synced from {company.integration.erpSystem} and
                        cannot be edited here. To update company information, please make changes in your
                        ERP system and they will be reflected here after the next sync.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Company Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Company Information
                    </CardTitle>
                    <CardDescription>Basic company details and registration</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Company Code</label>
                        <p className="text-base font-mono mt-1">{company.code}</p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Trading Name</label>
                        <p className="text-base font-semibold mt-1">{company.name}</p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Legal Name</label>
                        <p className="text-base mt-1">{company.legalName}</p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Company Registration Number
                        </label>
                        <p className="text-base font-mono mt-1">{company.registrationNumber || "—"}</p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">VAT Number</label>
                        <p className="text-base font-mono mt-1">{company.vatNumber || "—"}</p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Tax Number</label>
                        <p className="text-base font-mono mt-1">{company.taxNumber || "—"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Address Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Address & Contact
                    </CardTitle>
                    <CardDescription>Company location and contact details</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Address</label>
                        <div className="mt-1 space-y-1">
                          <p className="text-base">{company.address.line1}</p>
                          {company.address.line2 && <p className="text-base">{company.address.line2}</p>}
                          <p className="text-base">{company.address.city}</p>
                          {company.address.county && <p className="text-base">{company.address.county}</p>}
                          <p className="text-base font-semibold">{company.address.postcode}</p>
                          <p className="text-base">{company.address.country}</p>
                        </div>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Phone</label>
                        <p className="text-base mt-1 flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          {company.contact.phone}
                        </p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Email</label>
                        <p className="text-base mt-1 flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {company.contact.email}
                        </p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Website</label>
                        <p className="text-base mt-1 flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          {company.contact.website}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* System Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      System Settings
                    </CardTitle>
                    <CardDescription>Currency, timezone, and date formats</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Display Currency</label>
                        <Select value={currency.code} onValueChange={(value) => {
                          setCurrency(value);
                          toast.success(`Currency changed to ${value}`);
                        }}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {allCurrencies.map((curr) => (
                              <SelectItem key={curr.code} value={curr.code}>
                                {curr.symbol} {curr.code} - {curr.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          This affects how prices are displayed throughout the app
                        </p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">ERP Currency</label>
                        <p className="text-base mt-1">
                          {company.settings.currency} ({company.settings.currencySymbol})
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Synced from {company.integration.erpSystem}
                        </p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Timezone</label>
                        <p className="text-base mt-1">{company.settings.timezone}</p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Date Format</label>
                        <p className="text-base mt-1">{company.settings.dateFormat}</p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Fiscal Year</label>
                        <p className="text-base mt-1">
                          {company.settings.fiscalYearStart} to {company.settings.fiscalYearEnd}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Integration Status */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Integration Status
                    </CardTitle>
                    <CardDescription>ERP system connection details</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">ERP System</label>
                        <p className="text-base mt-1">{company.integration.erpSystem}</p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Version</label>
                        <p className="text-base mt-1">{company.integration.erpVersion}</p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Status</label>
                        <div className="mt-1">{getSyncStatusBadge(company.integration.syncStatus)}</div>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Last Sync</label>
                        <p className="text-base mt-1">{formatDate(company.integration.lastSyncDate)}</p>
                      </div>
                      <Separator />
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Data Source</label>
                        <p className="text-base mt-1">{company.metadata.dataSource}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Tax Configuration Tab */}
            <TabsContent value="tax" className="space-y-6 mt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Tax Rates</h3>
                  <p className="text-sm text-muted-foreground">
                    Manage VAT and tax rates for your stock items
                  </p>
                </div>
                <Button onClick={handleAdd}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tax Rate
                </Button>
              </div>

              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Default</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taxRates.map((taxRate) => (
                      <TableRow key={taxRate.id}>
                        <TableCell className="font-medium">{taxRate.name}</TableCell>
                        <TableCell>{taxRate.rate}%</TableCell>
                        <TableCell className="text-muted-foreground">{taxRate.description}</TableCell>
                        <TableCell>
                          {taxRate.isActive ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              <Check className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <X className="h-3 w-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {taxRate.isDefault ? (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetDefault(taxRate)}
                            >
                              Set as Default
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(taxRate)}
                            >
                              {taxRate.isActive ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(taxRate)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(taxRate)}
                              disabled={taxRate.isDefault}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add/Edit Tax Rate Dialog */}
      <Dialog open={isAddDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsAddDialogOpen(false);
          setIsEditDialogOpen(false);
          setSelectedTaxRate(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isAddDialogOpen ? 'Add Tax Rate' : 'Edit Tax Rate'}</DialogTitle>
            <DialogDescription>
              {isAddDialogOpen
                ? 'Create a new tax rate for your stock items'
                : 'Update the tax rate details'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Standard VAT"
              />
              {formErrors.name && (
                <p className="text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate">Rate (%) *</Label>
              <Input
                id="rate"
                type="number"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) })}
                min="0"
                max="100"
                step="0.01"
              />
              {formErrors.rate && (
                <p className="text-sm text-red-600">{formErrors.rate}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="isDefault"
                checked={formData.isDefault}
                onCheckedChange={(checked) => setFormData({ ...formData, isDefault: checked })}
              />
              <Label htmlFor="isDefault">Set as default tax rate</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                setIsEditDialogOpen(false);
                setSelectedTaxRate(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={isAddDialogOpen ? handleSaveAdd : handleSaveEdit}>
              {isAddDialogOpen ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tax Rate</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTaxRate?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
